/**
 * Charge Schedule Preview
 *
 * Shows a table of payment tranches based on the selected supplier's payment terms,
 * PO amount, tariff rate, placement date, and ETD.
 * Supports per-tranche payment date overrides via inline date pickers.
 */

import React from "react";
import moment from "moment";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Supplier, PaymentTranche } from "models/CashflowModel";

interface ChargeSchedulePreviewProps {
  supplier: Supplier;
  amount: number | null;
  tariffRatePercent: number | null;
  placementDate: string | null;
  etd: string | null;
  paymentDateOverrides?: Record<string, string> | null;
  onSetPaymentDateOverride?: (trancheOrder: number, date: string | null) => void;
  paidTranches?: Record<string, boolean> | null;
  onTogglePaidTranche?: (trancheOrder: number, paid: boolean) => void;
  isUpdatingPaid?: boolean;
}

export default function ChargeSchedulePreview({
  supplier,
  amount,
  tariffRatePercent,
  placementDate,
  etd,
  paymentDateOverrides,
  onSetPaymentDateOverride,
  paidTranches,
  onTogglePaidTranche,
  isUpdatingPaid,
}: ChargeSchedulePreviewProps): JSX.Element | null {
  const tranches = supplier.paymentTerms || [];
  if (tranches.length === 0) return null;

  const getPaymentTypeLabel = (tranche: PaymentTranche, index: number, total: number): string => {
    if (total === 1) return "Full Payment";
    if (index === 0) return "Deposit";
    if (index === total - 1) return "Balance";
    return `Payment ${index + 1}`;
  };

  const getTriggerLabel = (tranche: PaymentTranche): string => {
    if (tranche.triggerType === "upon_placement") return "Deposit (upon placement)";
    if (tranche.triggerType === "on_etd") return "On docs (ETD)";
    if (tranche.triggerType === "x_days_after_etd") {
      return `ETD + ${tranche.daysAfterEtd || 0} days`;
    }
    return tranche.triggerType;
  };

  const getCalculatedPaymentDate = (tranche: PaymentTranche): string | null => {
    if (tranche.triggerType === "upon_placement") {
      return placementDate || null;
    }
    if (tranche.triggerType === "on_etd") {
      return etd || null;
    }
    if (tranche.triggerType === "x_days_after_etd" && etd) {
      return moment(etd).add(tranche.daysAfterEtd || 0, "days").format("YYYY-MM-DD");
    }
    return null;
  };

  const baseAmount = amount || 0;
  const tariff = (tariffRatePercent || 0) / 100;
  const totalWithTariff = baseAmount + baseAmount * tariff;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, position: 'relative' }}>
      {isUpdatingPaid && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Charge Schedule
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: "text.secondary", py: 0.5 }}>Payment</TableCell>
            <TableCell sx={{ color: "text.secondary", py: 0.5 }}>Trigger</TableCell>
            <TableCell sx={{ color: "text.secondary", py: 0.5 }}>Date</TableCell>
            <TableCell align="right" sx={{ color: "text.secondary", py: 0.5 }}>Amount</TableCell>
            {onTogglePaidTranche && (
              <TableCell align="center" sx={{ color: "text.secondary", py: 0.5 }}>Paid</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {tranches.map((tranche, idx) => {
            const calculatedDate = getCalculatedPaymentDate(tranche);
            const overrideDate = paymentDateOverrides?.[String(tranche.trancheOrder)] ?? null;
            const displayDate = overrideDate || calculatedDate;
            const trancheAmount = totalWithTariff * tranche.percentage;
            const missingDate = !displayDate && (
              (tranche.triggerType === "upon_placement" && !placementDate) ||
              ((tranche.triggerType === "on_etd" || tranche.triggerType === "x_days_after_etd") && !etd)
            );
            return (
              <TableRow key={idx}>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getPaymentTypeLabel(tranche, idx, tranches.length)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getTriggerLabel(tranche)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  {onSetPaymentDateOverride ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <DatePicker
                        value={displayDate ? moment(displayDate) : null}
                        onChange={(newValue: any) => {
                          if (newValue && newValue.isValid()) {
                            onSetPaymentDateOverride(tranche.trancheOrder, newValue.format("YYYY-MM-DD"));
                          } else {
                            onSetPaymentDateOverride(tranche.trancheOrder, null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { width: 150 },
                            placeholder: "Pick date",
                          },
                        }}
                      />
                      {overrideDate && (
                        <>
                          <Chip
                            label="overridden"
                            size="small"
                            color="info"
                            variant="filled"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => onSetPaymentDateOverride(tranche.trancheOrder, null)}
                            title="Reset to calculated date"
                          >
                            <ClearIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {displayDate ? moment(displayDate).format("MM/DD/YY") : "—"}
                      </Typography>
                      {missingDate && (
                        <Typography variant="caption" color="text.disabled">
                          {tranche.triggerType === "upon_placement" ? "Set placement date" : "Set ETD"}
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {baseAmount > 0 ? `$${Math.round(trancheAmount).toLocaleString()}` : "—"}
                  </Typography>
                </TableCell>
                {onTogglePaidTranche && (
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={!!paidTranches?.[String(tranche.trancheOrder)]}
                      onChange={(e) => onTogglePaidTranche(tranche.trancheOrder, e.target.checked)}
                      disabled={isUpdatingPaid}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}
